import { z } from "zod";

/** Decimal-baht string: optional leading "-", 1+ integer digits, 0-2 decimal digits. */
const MONEY_STRING_RE = /^-?\d+(\.\d{1,2})?$/;

/**
 * Validates a decimal-baht money string (the wire format since the
 * 2026-07-24 satang->decimal-string migration, e.g. "150.50", "-25.00").
 * Tolerant of 0-2 decimal digits on read; callers wanting to enforce exactly
 * 2 on write should format before submitting, not rely on this to pad.
 * Takes the i18n key for a format violation so each caller supplies its own.
 */
export function moneyStringSchema(message: string): z.ZodString {
  return z.string().regex(MONEY_STRING_RE, message);
}

/** Parses a decimal-baht money string into a number, for arithmetic/comparisons. */
export function parseMoneyAmount(amount: string): number {
  return Number(amount);
}

/**
 * Formats a baht (or other currency) amount as a human-readable string.
 * THB/empty → "฿1,234.56"; PHP → "₱1,234.56"; malformed ISO-4217 codes
 * degrade to "<CODE> 1,234.56".
 */
export function formatCurrencyAmount(amount: number, currency?: string): string {
  if (!currency || currency === "THB") {
    return (
      "฿" +
      amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }
  try {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
    }).format(amount);
    // Detect if the currency was not recognized (generic ¤ symbol used)
    if (formatted.includes("¤")) {
      return `${currency} ${amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    return formatted;
  } catch {
    return `${currency} ${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}

/**
 * Formats a decimal-baht money string (e.g. "150.50", "-25.00") — the wire
 * format for every money field since 2026-07-24.
 */
export function moneyFromString(amount: string, currency?: string): string {
  return formatCurrencyAmount(parseMoneyAmount(amount), currency);
}
